import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { toJpeg, toBlob } from 'html-to-image';
import { X, Users, Trophy, History, Star, MessageCircle, CalendarDays, ExternalLink, TrendingUp, ArrowRight, Info, Share2, UserPlus, UserCheck, UserX, BadgeCheck, Phone, Lock } from 'lucide-react';
import { PlayerCardPremium } from './PlayerCardPremium';
import { Player, Match, Team, Config, Tournament, UserProfile } from '../../types';
import { getCategoryStyles } from '../../lib/categoryStyles';
import { formatPairName } from '../../lib/utils';
import { MatchCard } from '../match/MatchCard';
import { doc, updateDoc, getDoc, collection, setDoc, onSnapshot, query, where, getDocs, limit, documentId, addDoc, deleteDoc } from 'firebase/firestore';
import { db, getCollectionPath } from '../../lib/firebase';
import { useBackButton } from '../../hooks/useBackButton';

interface PlayerDetailModalProps {
  player: Player | null;

  players: Player[];
  currentUserId?: string;
  currentUserProfile?: UserProfile | null;
  onMatchClick: (match: Match) => void;
  onPlayerClick: (playerId: string) => void;
  onContactClick?: (playerId: string, playerName: string, playerPhoto: string) => void;
  onClose: () => void;
  config: Config;
  forceShowContactInfo?: boolean;
}

const hasActiveMatchContext = (currentUserId: string, playerId: string, matches: Match[], teams: Team[]) => {
  return matches.some(m => {
    if (m.status === 'completed' || m.status === 'finished') return false;
    const t1 = teams.find(t => t.id === m.team1Id);
    const t2 = teams.find(t => t.id === m.team2Id);
    const isPlayerInT1 = t1?.player1Id === playerId || t1?.player2Id === playerId || t1?.player3Id === playerId;
    const isPlayerInT2 = t2?.player1Id === playerId || t2?.player2Id === playerId || t2?.player3Id === playerId;
    const isCurrentUserInT1 = t1?.player1Id === currentUserId || t1?.player2Id === currentUserId || t1?.player3Id === currentUserId;
    const isCurrentUserInT2 = t2?.player1Id === currentUserId || t2?.player2Id === currentUserId || t2?.player3Id === currentUserId;
    return (isPlayerInT1 || isPlayerInT2) && (isCurrentUserInT1 || isCurrentUserInT2);
  });
};

export function PlayerDetailModal({
  player,
  players,
  currentUserId,
  currentUserProfile,
  onMatchClick,
  onPlayerClick,
  onContactClick,
  onClose,
  config,
  forceShowContactInfo
}: PlayerDetailModalProps) {
  const backdropVariants = {
    hidden: {
      backgroundColor: "rgba(0, 0, 0, 0)",
      backdropFilter: "blur(0px)",
    },
    visible: {
      backgroundColor: "rgba(0, 0, 0, 0.4)",
      backdropFilter: "blur(8px)",
      transition: {
        duration: 0.2,
        when: "beforeChildren"
      }
    },
    exit: {
      backgroundColor: "rgba(0, 0, 0, 0)",
      backdropFilter: "blur(0px)",
      transition: {
        duration: 0.15,
        when: "afterChildren"
      }
    }
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      y: 10,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.25,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: 10,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }
  };

  const [activeTab, setActiveTab] = useState<'history' | 'tournaments'>('tournaments');
  const [showStreakInfo, setShowStreakInfo] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useBackButton(true, onClose);

  // Self-load the player's matches and teams
  const [matches, setMatches] = useState<Match[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    if (!player?.id) return;
    const statsRef = doc(db, getCollectionPath('playerStats'), player.id);
    const unsub = onSnapshot(statsRef, snap => {
      if (snap.exists()) {
        setStats(snap.data());
      } else {
        setStats(null);
      }
    });
    return () => unsub();
  }, [player?.id]);

  useEffect(() => {
    if (!player?.id) return;
    const fetchPlayerMatches = async () => {
      const q = query(
        collection(db, getCollectionPath('matches')),
        where('participantIds', 'array-contains', player.id),
        limit(100)
      );
      const snap = await getDocs(q);
      const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() } as Match));
      setMatches(loaded);

      // Collect team IDs and load them
      const teamIds = new Set<string>();
      loaded.forEach(m => {
        if (m.team1Id) teamIds.add(m.team1Id);
        if (m.team2Id) teamIds.add(m.team2Id);
      });
      const newTeams: Team[] = [];
      const allIds = Array.from(teamIds);
      if (allIds.length > 0) {
        for (let i = 0; i < allIds.length; i += 30) {
          const batch = allIds.slice(i, i + 30);
          const tSnap = await getDocs(query(
            collection(db, getCollectionPath('teams')),
            where(documentId(), 'in', batch)
          ));
          tSnap.docs.forEach(d => newTeams.push({ id: d.id, ...d.data() } as Team));
        }
        setTeams(newTeams);
      }
    };
    fetchPlayerMatches();
  }, [player?.id]);

  useEffect(() => {
    const updateScale = () => {
      const width = window.innerWidth;
      const padding = 32;
      const baseWidth = 340;
      if (width < baseWidth + padding) {
        setScale((width - padding) / baseWidth);
      } else {
        setScale(1);
      }
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);



  if (!player) return null;

  const catStyles = getCategoryStyles(player.category);

  const getStarsCount = (category: string) => {
    const cat = (category || '').toLowerCase();
    if (cat.includes('1ra') || cat.includes('primera')) return 8;
    if (cat.includes('2da') || cat.includes('segunda')) return 7;
    if (cat.includes('3ra') || cat.includes('tercera')) return 6;
    if (cat.includes('4ta') || cat.includes('cuarta')) return 5;
    if (cat.includes('5ta') || cat.includes('quinta')) return 4;
    if (cat.includes('6ta') || cat.includes('sexta')) return 3;
    if (cat.includes('7ma') || cat.includes('septima') || cat.includes('séptima')) return 2;
    if (cat.includes('8va') || cat.includes('octava')) return 1;
    return 0;
  };

  const [sedes, setSedes] = useState<any[]>([]);
  useEffect(() => {
    const fetchSedes = async () => {
      const q = query(collection(db, getCollectionPath('users')), where('role', 'in', ['admin', 'superadmin']));
      const snap = await getDocs(q);
      setSedes(snap.docs.map(d => ({ uid: d.id, ...d.data() })));
    };
    fetchSedes();
  }, []);

  const playerMatches = matches.filter(m => {
    if (m.status !== 'completed') return false;
    const t1 = teams.find(t => t.id === m.team1Id);
    const t2 = teams.find(t => t.id === m.team2Id);
    return t1?.player1Id === player.id || t1?.player2Id === player.id || t1?.player3Id === player.id ||
      t2?.player1Id === player.id || t2?.player2Id === player.id || t2?.player3Id === player.id;
  });

  // Compute best rank across all circuits
  let bestRank = 0;
  let bestCircuitName = 'Global';
  let bestIsGlobal = true;
  let bestCircuitLogo = '';

  const sortedGlobal = [...players].filter(p => !p.isGuest).sort((a, b) => (b.rankingPoints?.global ?? (b.points || 0)) - (a.rankingPoints?.global ?? (a.points || 0)));
  const globalRank = sortedGlobal.findIndex(p => p.id === player.id) + 1;
  const globalPoints = player.rankingPoints?.global ?? (player.points || 0);

  if (globalPoints > 0) {
    bestRank = globalRank;
  }

  if (player.rankingPoints?.clubs) {
    Object.entries(player.rankingPoints.clubs).forEach(([clubId, points]) => {
      if (points > 0) {
        const sortedClub = [...players].filter(p => !p.isGuest).sort((a, b) => (b.rankingPoints?.clubs?.[clubId] || 0) - (a.rankingPoints?.clubs?.[clubId] || 0));
        const clubRank = sortedClub.findIndex(p => p.id === player.id) + 1;
        if (clubRank > 0 && (bestRank === 0 || clubRank < bestRank)) {
          bestRank = clubRank;
          const club = sedes.find(s => s.uid === clubId);
          bestCircuitName = club?.name || 'Sede Local';
          bestIsGlobal = false;
          bestCircuitLogo = club?.photoUrl || '';
        }
      }
    });
  }

  const isUnranked = bestRank === 0;

  const wins = playerMatches.filter(m => {
    const myTeam = teams.find(t => (t.player1Id === player.id || t.player2Id === player.id || t.player3Id === player.id) && (t.id === m.team1Id || t.id === m.team2Id));
    return m.winnerId === myTeam?.id;
  }).length;

  const losses = playerMatches.length - wins;
  const winRate = playerMatches.length > 0 ? (wins / playerMatches.length) * 100 : 0;

  const streak = stats?.currentWinStreak ?? (player.winStreak || 0);
  const isEnRacha = streak >= 3;
  const multiplier = isEnRacha ? 1.5 : 1;

  const getRoundWeight = (round: string) => {
    const r = (round || '').toLowerCase();
    if (r.includes('fecha 1')) return 10;
    if (r.includes('fecha 2')) return 20;
    if (r.includes('fecha 3')) return 30;
    if (r.includes('fecha 4')) return 40;
    if (r.includes('fecha 5')) return 50;
    if (r.includes('fecha 6')) return 60;
    if (r.includes('dieciseisavos')) return 90;
    if (r.includes('octavos')) return 100;
    if (r.includes('cuartos')) return 110;
    if (r.includes('semi')) return 120;
    if (r.includes('final')) return 130;
    return 999;
  };

  const sortMatchesByPriority = (matchesToSort: Match[]) => {
    return [...matchesToSort].sort((a, b) => {
      // 1. By Date
      if (a.date && b.date) {
        const [d1, m1] = a.date.split('/').map(Number);
        const [d2, m2] = b.date.split('/').map(Number);
        if (m1 && m2 && d1 && d2) {
          if (m1 !== m2) return m1 - m2;
          if (d1 !== d2) return d1 - d2;
        }
      }

      // 2. By Round weight
      const w1 = getRoundWeight(a.tournamentRound || '');
      const w2 = getRoundWeight(b.tournamentRound || '');
      if (w1 !== w2) return w1 - w2;

      return (b.createdAt || 0) - (a.createdAt || 0);
    });
  };

  const pendingPlayerMatches = sortMatchesByPriority(matches.filter(m => {
    if (m.status !== 'pending') return false;
    const t1 = teams.find(t => t.id === m.team1Id);
    const t2 = teams.find(t => t.id === m.team2Id);
    return t1?.player1Id === player.id || t1?.player2Id === player.id || t1?.player3Id === player.id ||
      t2?.player1Id === player.id || t2?.player2Id === player.id || t2?.player3Id === player.id;
  }));

  const lastMatches = [...playerMatches].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).slice(0, 10);

  const trophies = {
    oro: player.achievements?.filter(a => a.type === 'oro').length || 0,
    plata: player.achievements?.filter(a => a.type === 'plata').length || 0,
  };

  const shareToWhatsapp = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!cardRef.current) return;

    try {
      setIsCapturing(true);

      // Brief pause to ensure UI is ready
      await new Promise(resolve => setTimeout(resolve, 300));

      const dataUrl = await toJpeg(cardRef.current, {
        quality: 0.8,
        pixelRatio: 1.5,
        backgroundColor: '#000',
        cacheBust: true,
      });

      const fileName = `rpt-${player.name.replace(/\s+/g, '-').toLowerCase()}.jpg`;

      // Priority 1: Native Web Share (Fastest and best UX)
      if (navigator.share && navigator.canShare) {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], fileName, { type: 'image/jpeg' });

        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: `Tarjeta RPT - ${player.name}`,
            text: `🎾 ¡Mira la tarjeta oficial de ${player.name} en RPT!`
          });
          return;
        }
      }

      // Priority 2: Direct Download + Instant Redirect
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Short delay before redirect to allow download to trigger
      setTimeout(() => {
        const text = `🎾 ¡Mira la tarjeta de ${player.name} en RPT!\n\n(Adjunta la imagen que acabas de descargar)`;
        window.location.href = `https://wa.me/?text=${encodeURIComponent(text)}`;
      }, 600);

    } catch (err) {
      console.error('Share failed:', err);
      // Final fallback: just open WhatsApp with text
      const text = `🎾 ¡Mira la tarjeta de ${player.name} en RPT!\n\n${window.location.origin}`;
      window.location.href = `https://wa.me/?text=${encodeURIComponent(text)}`;
    } finally {
      setIsCapturing(false);
    }
  };

  return (
    <motion.div
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="fixed inset-0 z-[110] flex flex-col items-center justify-start p-4 overflow-y-auto"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="fixed top-6 right-6 z-[130] p-2 bg-black/40 hover:bg-white/10 rounded-full text-white transition-all border border-white/10 backdrop-blur-md active:scale-95"
      >
        <X size={24} />
      </button>
      <AnimatePresence>
        {showStreakInfo && (
          <div
            className="fixed inset-0 z-[120] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowStreakInfo(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="w-full max-w-sm bg-zinc-900 border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Background Glow */}
              <div className="absolute -top-24 -right-24 w-48 h-48 bg-orange-500/20 blur-[80px]" />

              <div className="relative z-10 flex flex-col items-center text-center gap-6">
                <div className="w-16 h-16 bg-orange-500/20 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/10">
                  <span className="text-3xl">🔥</span>
                </div>

                <div className="space-y-2">
                  <h3 className="text-xl font-black uppercase italic tracking-wider text-white">¡Modo On Fire!</h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {player.name} está en una racha de <span className="text-orange-400 font-bold">{streak}</span> partidos ganados consecutivamente.
                  </p>
                </div>

                <div className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-4">
                  <div className="w-10 h-10 bg-theme-primary/20 rounded-xl flex items-center justify-center theme-primary-text">
                    <TrendingUp size={20} />
                  </div>
                  <div className="text-left">
                    <p className="text-[10px] font-black uppercase tracking-widest theme-primary-text">Multiplicador</p>
                    <p className="text-lg font-black italic text-white">{multiplier}X Puntos</p>
                  </div>
                </div>

                <button
                  onClick={() => setShowStreakInfo(false)}
                  className="w-full py-4 bg-white text-black font-black uppercase tracking-widest rounded-2xl hover:bg-theme-primary transition-colors active:scale-95"
                >
                  Entendido
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <div 
        className="relative mt-40 mb-12 origin-top transition-transform duration-300"
        style={{ transform: `scale(${scale})` }}
      >
        <motion.div
          variants={cardVariants}
          className="flex flex-col items-center gap-6 max-w-full w-fit relative"
          onClick={(e) => e.stopPropagation()}
        >
        {/* External Top Actions */}
        {(() => {
          const isOwner = currentUserId === player.id;
          const hasMatch = currentUserId ? hasActiveMatchContext(currentUserId, player.id, matches, teams) : false;
          const isAdminUser = currentUserProfile?.role === 'admin' || currentUserProfile?.role === 'superadmin' || currentUserProfile?.role === 'staff';
          const canSeePhone = isOwner || hasMatch || isAdminUser || forceShowContactInfo;
          const isRealUser = !(player.isGuest || player.id.startsWith('guest_') || player.id.length !== 28);
          const showActions = isRealUser && currentUserId && currentUserId !== player.id;
          const canChat = isAdminUser;

          return (
            <div className="absolute -top-[8rem] left-0 right-0 flex flex-col items-center justify-end gap-3 z-[115]">

              {/* Row 2: Phone, Chat, Share */}
              <div className="relative w-full h-10 flex items-center justify-center">
                <div className="flex items-center gap-2 justify-center">
                  {/* Phone */}
                  {!player.phone ? (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md">
                      <Phone size={12} className="text-white/30" />
                      <span className="text-[10px] font-bold text-white/50">Sin teléfono</span>
                    </div>
                  ) : !canSeePhone ? (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md" title="Teléfono oculto por privacidad (solo amigos y rivales)">
                      <Lock size={12} className="text-white/40" />
                      <span className="text-[10px] font-bold text-white/50 uppercase tracking-widest">Privado</span>
                    </div>
                  ) : (
                    <a 
                      href={`https://wa.me/${player.phone.replace(/[^0-9]/g, '')}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 border border-white/20 backdrop-blur-md hover:bg-white/20 transition-colors"
                      title="Contactar por WhatsApp"
                    >
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="#25D366">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      <span className="text-[10px] font-bold text-white">{player.phone}</span>
                    </a>
                  )}

                  {/* Chat */}
                  {showActions && canChat && onContactClick && (
                    <button
                      onClick={() => onContactClick(player.id, player.name, player.photoUrl || '')}
                      className="group flex items-center gap-2 px-3 py-1.5 rounded-xl transition-all duration-300 border backdrop-blur-md active:scale-95 bg-white text-black hover:bg-theme-primary shadow-xl border-transparent"
                      title="Abrir chat privado."
                    >
                      <MessageCircle size={14} className="opacity-70 group-hover:opacity-100" />
                      <span className="text-[9px] font-black uppercase tracking-widest">Chat</span>
                    </button>
                  )}

                  {/* Share */}
                  <button
                    onClick={shareToWhatsapp}
                    disabled={isCapturing}
                    className={`group flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-300 border backdrop-blur-md active:scale-95 ${isCapturing
                        ? 'bg-white/5 border-white/10 text-white/40 cursor-wait'
                        : 'bg-transparent text-white border-transparent hover:bg-white/10'
                      }`}
                    title="Compartir tarjeta"
                  >
                    {isCapturing ? (
                      <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Share2 size={14} className="opacity-80 group-hover:opacity-100" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Main Card Section */}
        <PlayerCardPremium
          player={player}
          playerMatches={playerMatches}
          matchesCount={stats?.totalCompletedMatches ?? playerMatches.length}
          wins={stats?.cumulativeWins ?? wins}
          losses={stats ? (stats.totalCompletedMatches - stats.cumulativeWins) : losses}
          winRate={stats ? (stats.totalCompletedMatches > 0 ? (stats.cumulativeWins / stats.totalCompletedMatches) * 100 : 0) : winRate}
          categoryRank={bestRank}
          circuitName={bestIsGlobal ? '' : bestCircuitName}
          circuitLogo={bestIsGlobal ? '' : bestCircuitLogo}
          isUnranked={isUnranked}
          config={config}
          isEnRacha={isEnRacha}
          onStreakClick={() => setShowStreakInfo(true)}
          innerRef={cardRef}
        />

        {/* Annexed History Section (Bottom) */}
        {/* Annexed History Section (Bottom) */}
        <div
          className="relative w-[340px] bg-[#1e293b] rounded-[20px] overflow-hidden shadow-[0_30px_90px_rgba(0,0,0,0.9)] animate-in slide-in-from-bottom-4 duration-500"
          onClick={(e) => e.stopPropagation()}
        >
          {/* === DYNAMIC METALLIC FRAME (SILVER) === */}
          <div className="absolute inset-0 pointer-events-none z-[60] rounded-[20px]">
            <div
              className="absolute inset-0 rounded-[20px] shadow-[inset_0_2px_3px_rgba(255,255,255,0.3),inset_0_-2px_3px_rgba(0,0,0,0.7)]"
              style={{
                background: `linear-gradient(225deg, #000 0%, rgba(255,255,255,0.05) 20%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.05) 80%, #000 100%)`,
                padding: '6px',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'destination-out',
                maskComposite: 'exclude',
              }}
            />
            <div
              className="absolute inset-0 rounded-[20px] overflow-hidden"
              style={{
                padding: '6px',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'destination-out',
                maskComposite: 'exclude',
              }}
            >
              <div className="absolute inset-[-200%] bg-gradient-to-r from-transparent via-white/10 to-transparent animate-[shimmer_5s_infinite_linear] transform rotate-[-45deg]" />
            </div>
            <div className="absolute inset-[6px] rounded-[14px] pointer-events-none border-t border-white/20 border-b border-black/50 border-x border-white/5" />
          </div>

          {/* === ASCENSION PATTERN (SILVER) === */}
          <div className="absolute inset-0 opacity-[0.02] pointer-events-none rounded-[20px]" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '16px 16px'
          }} />

          <div className="absolute inset-0 pointer-events-none z-[1] rounded-[20px]"
            style={{
              WebkitMaskImage: `
                linear-gradient(to bottom, transparent 0%, black 20px, black calc(100% - 20px), transparent 100%),
                linear-gradient(to right, transparent 0%, black 20px, black calc(100% - 20px), transparent 100%)
              `,
              maskImage: `
                linear-gradient(to bottom, transparent 0%, black 20px, black calc(100% - 20px), transparent 100%),
                linear-gradient(to right, transparent 0%, black 20px, black calc(100% - 20px), transparent 100%)
              `,
              WebkitMaskComposite: 'source-in',
              maskComposite: 'intersect'
            }}
          >
            <div className="absolute inset-0"
              style={{
                background: `linear-gradient(to bottom, rgba(255,255,255,0.15) 0%, #000000 100%)`,
                WebkitMaskImage: `
                  url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath d='M4 14 L10 8 L16 14' fill='none' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
                `,
                maskImage: `
                  url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath d='M4 14 L10 8 L16 14' fill='none' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
                `,
                WebkitMaskSize: '14px 14px',
                maskSize: '14px 14px',
                WebkitMaskPosition: '0 0, 7px 7px',
                maskPosition: '0 0, 7px 7px',
                WebkitMaskRepeat: 'round',
                maskRepeat: 'round',
                mixBlendMode: 'screen',
                opacity: 0.2
              }}
            />
          </div>

          {/* === COMPOSITE TECHNICAL PERIMETER === */}
          <div className="absolute inset-[6px] pointer-events-none z-30">
            <div className="relative w-full h-full border border-white/5 rounded-[14px] overflow-hidden">
              {/* Segmented HUD Lines */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/3 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              
              {/* Corner Bracket Accents (Silver/White) */}
              <div
                className="absolute top-0 left-0 w-4 h-4 border-t border-l rounded-tl-[8px] border-white/10"
                style={{
                  WebkitMaskImage: 'radial-gradient(circle at top left, black 20%, transparent 80%)',
                  maskImage: 'radial-gradient(circle at top left, black 20%, transparent 80%)'
                }}
              />
              <div
                className="absolute bottom-0 right-0 w-4 h-4 border-b border-r rounded-br-[8px] border-white/10"
                style={{
                  WebkitMaskImage: 'radial-gradient(circle at bottom right, black 20%, transparent 80%)',
                  maskImage: 'radial-gradient(circle at bottom right, black 20%, transparent 80%)'
                }}
              />
            </div>
          </div>

          {/* Professional Form Content Section - Horizontal Layout */}
          <div className="relative z-40 px-5 py-4">
            <div className="flex items-center justify-between gap-3">
              {/* Title Section (Left) */}
              <div className="flex items-center gap-3">
                <h4 className="text-[9px] font-black uppercase tracking-[0.3em] text-white/50 leading-tight text-left">
                  Forma<br/>Reciente
                </h4>
                <div className="flex items-center opacity-40" title="De izquierda (más antiguo) a derecha (más reciente)">
                  <div className="flex items-center">
                    <div className="w-6 h-[1.5px] bg-white" />
                    <ArrowRight size={12} className="-ml-1.5 text-white" />
                  </div>
                </div>
              </div>

              {/* Technical Skewed Form Indicators (Right) */}
              <div className="flex items-center gap-1.5">
                {Array.from({ length: 5 }).map((_, i) => {
                  const match = lastMatches[4 - i];

                  if (!match) {
                    return (
                      <div 
                        key={`empty-${i}`} 
                        className="w-7 h-9 flex flex-col items-center justify-center transform -skew-x-[15deg] overflow-hidden rounded-sm opacity-40 bg-white/5"
                        style={{ border: '1px dashed rgba(255,255,255,0.15)' }}
                      >
                        <svg className="w-2.5 h-2.5 text-white/30 transform skew-x-[15deg]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    );
                  }

                  const t1 = teams.find(t => t.id === match.team1Id);
                  const t2 = teams.find(t => t.id === match.team2Id);
                  const myTeam = (t1?.player1Id === player.id || t1?.player2Id === player.id || t1?.player3Id === player.id) ? t1 : t2;
                  const won = match.winnerId === myTeam?.id;
                  const isLatest = i === 4;

                  return (
                    <motion.div
                      key={match.id}
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      className={`group relative w-7 h-9 flex flex-col transform -skew-x-[15deg] overflow-hidden rounded-sm shadow-lg transition-all duration-300 cursor-pointer ${won ? 'shadow-emerald-500/20' : 'opacity-90 hover:opacity-100'} ${isLatest ? 'scale-105 z-10 ring-1 ring-white/30 animate-[pulse_1s_infinite]' : ''}`}
                      style={{
                        backgroundColor: won ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255,255,255,0.05)',
                        border: won ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(255,255,255,0.15)'
                      }}
                    >
                      {/* Status Bar */}
                      <div className={`h-[8px] w-full flex items-center justify-center ${won ? 'bg-emerald-500/80' : 'bg-white/20'}`}>
                        <div className="w-1 h-[2px] rounded-full bg-white/60 transform skew-x-[15deg]" />
                      </div>

                      {/* Value Section */}
                      <div className="flex-1 flex items-center justify-center w-full">
                        <span className={`text-[12px] font-black italic transform skew-x-[15deg] leading-none ${won ? 'text-emerald-400' : 'text-white/60'}`}>
                          {won ? 'W' : 'L'}
                        </span>
                      </div>

                      {/* Premium Hover Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-3 py-2 bg-black/95 backdrop-blur-xl border border-white/20 rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-50 whitespace-nowrap shadow-2xl scale-90 group-hover:scale-100 origin-bottom transform skew-x-[15deg]">
                        <p className="text-[7px] font-black uppercase tracking-[0.2em] text-white/40 mb-1">{match.tournamentName}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-black italic theme-primary-text tracking-widest">{match.s1} {match.s2} {match.s3}</span>
                          <div className={`px-1.5 py-0.5 rounded-full text-[6px] font-black uppercase ${won ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                            {won ? 'Victoria' : 'Derrota'}
                          </div>
                        </div>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-black/95" />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Verification Tag */}
        {((player as any).categoryValidated || player.isValidated) && (
          <div className="w-[340px] mt-4 pt-4 border-t border-white/5 flex items-center justify-center gap-2 relative">
            {/* Minimalist glow on the divider */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-[1px] bg-gradient-to-r from-transparent via-lime-500/20 to-transparent" />
            
            <div className="relative flex items-center justify-center overflow-hidden rounded-full shrink-0">
              <BadgeCheck className="text-lime-400 drop-shadow-[0_0_8px_rgba(163,230,53,0.5)]" size={24} />
              {/* Efecto barrido metálico solo sobre el ícono */}
              <div className="absolute inset-0 -translate-x-full animate-[shimmer_3s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent skew-x-12" />
            </div>
            <span className="text-[10px] md:text-xs font-black uppercase tracking-wider text-white text-center drop-shadow-md opacity-80">
              Categoría validada por: <span className="text-lime-400">{(player as any).validatedByCategoryBy === 'club' && (player as any).validatedByCategoryClubName
                ? (player as any).validatedByCategoryClubName
                : 'RPT oficial'}</span>
            </span>
          </div>
        )}

        </motion.div>
      </div>
    </motion.div>
  );
}


// trigger rebuild
